// Copyright TraderEvolution Global LTD. © 2017-2024. All rights reserved.

import { Resources } from "../../Commons/properties/Resources.js";
import { LinkedSystem } from "../misc/LinkedSystem.js";
import { TerceraAccountLookup, _AccountItemAll } from "../elements/Lookup/TerceraAccountLookup.js";
import { PositionsBalance } from "../../Commons/cache/PositionsBalance.js";
import { PositionsBalanceItem } from "../cache/PositionsBalanceItem.js";
import { ColouringModes } from "../elements/QuickTable/QuickTableColumn.js";
import { TerceraLinkControlConstants } from "../UtilsClasses/TerceraLinkControlConstants.js";
import { TerceraMenu } from "../elements/TerceraMenu.js";
import { PanelNames } from "../UtilsClasses/FactoryConstants.js";
import { ApplicationPanelNew } from "./ApplicationPanelNew.js";
import { ProductType } from "../../Utils/Instruments/ProductType.js";
import { DynProperty } from "../../Commons/DynProperty.js";
import { DataCache } from "../../Commons/DataCache.js";
import { ApplicationInfo } from "../../Commons/ApplicationInfo.js";


export let PositionsBalancePanel = ApplicationPanelNew.extend(
    {
        topPanelHeight: 27,
        data: function ()
        {
            return {
                isAccountLinkShow: true,
                isSymbolLinkShow: true,
                isShowExploreTheAppLink: ApplicationInfo.isExploreMode,
            };
        },
        ToolbarVisible: true,
        ShowTotals: false,
        moreThanOneTTKey: 'panel.positionsBalance.menu.MoreOneSymbolInfoDisabled.tt',     // #95439
        noOneTTKey: 'panel.positionsBalance.menu.NoSymbolInfoDisabled.tt',
        headerLocaleKey: 'panel.PositionsBalance',
        accLookup: null,
        delayAccounts: null,
        balances: null,
        inited: false
    });

PositionsBalancePanel.prototype.getType = function () { return PanelNames.PositionsBalancePanel };

PositionsBalancePanel.prototype.oncomplete = function ()
{
    ApplicationPanelNew.prototype.oncomplete.apply(this);

    this.DCEventsSubscribe()
    this.populateTableContextMenu()
    this.localize()

    this.UpdateShowTotalsStateChange(!!this.ShowTotals);
};

PositionsBalancePanel.prototype.AddAllRows = function ()  
{
    let qt = this.quickTableRactive.quickTable
    if (!qt) return

    qt.ClearAll()
    this.balances = {}

    let accInUse = this.getAccInUse();
    let positions = DataCache.getAllPositions();
    let positionsCorporateAction = DataCache.getAllPositionsCorporateAction();

    this.AddRows(positions, accInUse);
    this.AddRows(positionsCorporateAction, accInUse);
};

PositionsBalancePanel.prototype.AddRows = function (positions, accInUse)  
{
    for (let posId in positions)
    {
        let pos = positions[posId]

        if (accInUse && accInUse[pos.Account.AcctNumber])
            this.UpdateByPosition(pos)
    }
};

PositionsBalancePanel.prototype.UpdateByPosition = function (pos)
{
    let qt = this.quickTableRactive.quickTable
    if (!qt || !pos) return

    let ins = pos.Instrument,
        balanceID = ins.InstrumentTradableID,
        accs = this.getAccInUse(),
        row = null, item = null,
        isPT_CA = pos.ProductType === ProductType.CorporateAction

    if (isPT_CA)
        balanceID += "-b";

    let balance = this.balances[balanceID]
    if (!balance)
    {
        balance = new PositionsBalance(pos, accs)

        row = qt.AddItem(new PositionsBalanceItem(balance));
        row.visible = !balance.isEmpty
        item = row.item
        balance.rowID = row.id
        balance.isCorporateAction = isPT_CA;

        this.balances[balanceID] = balance
    }
    else
    {
        row = qt.rows[balance.rowID]
        item = row.item

        if (balance.Symbol !== ins.DisplayName() && !isPT_CA)
        {
            let correctSymbol = this.getSymbolToDisplay(ins, accs)
            if (InstrumentUtils.IsFullName(correctSymbol))
            {
                let newIns = DataCache.getInstrumentByName(correctSymbol)
                if (newIns)
                    balance.Symbol = newIns.DisplayName()
                row.visible = !balance.RecalculateByInstrument(newIns, accs)
            }
            else
            {
                balance.Symbol = correctSymbol
                row.visible = !balance.RecalculateByInstrument(ins, accs, balanceID)
            }
        }
        else
            row.visible = !item.UpdateByAccInUse(accs)
    }

    this.updatePanelHeader()
    qt.needRedrawBackground = true
}

PositionsBalancePanel.prototype.getSymbolToDisplay = function (instrument, accInUse)       // возвращает правильное значение Symbol c роутом или без, подробнее 5.16 https://docs.google.com/document/d/1PSk4o6ove1LdDFRV-IySw6vdGWGPQs2CfTgwjlw_A-g
{
    let DC = DataCache, accs = DC.getAccounts(),
        TID = instrument.InstrumentTradableID,
        insInteriorInUse = []

    for (let accId in accInUse)
    {
        let acc = accs[accId],
            positions = DataCache.getPositionsByAccount(acc)

        for (let posID in positions)
        {
            let pos = positions[posID],
                ins = pos.Instrument,
                insInteriorId = ins.GetInteriorID(),
                insTID = ins.InstrumentTradableID

            if (TID != insTID) continue

            if (insInteriorInUse.length > 0 && insInteriorInUse[0] != insInteriorId)
                return instrument.DisplayShortName()    // нашли для данных акков позиции с искомым инструментом на разных роутах

            insInteriorInUse.push(insInteriorId)
        }
    }

    return insInteriorInUse.length ? insInteriorInUse[0] : null
}

PositionsBalancePanel.prototype.getAccInUse = function ()      
{
    if (!this.accLookup)
        return null

    let accArr = [], selectedAccs = this.accLookup.get('selectedItem')
    if (selectedAccs._AccountItemAll)
    {
        let accounts = DataCache.getAccounts()
        for (let accID in accounts)
            accArr.push(accounts[accID])
    }
    else if (selectedAccs.arraySelected)
        accArr = selectedAccs.arraySelected;
    else
        accArr = [selectedAccs];


    let accUse = {}
    for (let i = 0; i < accArr.length; i++)
        accUse[accArr[i].AcctNumber] = true

    return accUse
}

PositionsBalancePanel.prototype.updatePanelHeader = function ()
{
    if (!this.quickTableRactive || !this.quickTableRactive.quickTable)
        return

    let qt = this.quickTableRactive.quickTable,
        //rowNumSt = '  (' + (DataCache.AccountsCount > 1 ? qt.visibleRowCount() + '/' : '') + this.rowCount + ')',
        rowNumSt = '  (' + qt.visibleRowCount() + ')',
        newHeader = Resources.getResource(this.headerLocaleKey) + rowNumSt

    this.set('header', newHeader);
};

PositionsBalancePanel.prototype.DCEventsSubscribe = function ()
{
    DataCache.OnAddPosition.Subscribe(this.UpdateByPosition, this)
    DataCache.OnUpdatePosition.Subscribe(this.UpdateByPosition, this)
    DataCache.OnRemovePosition.Subscribe(this.UpdateByPosition, this)

    //CorporateAction
    DataCache.OnAddCorporateAction.Subscribe(this.UpdateByPosition, this)
    DataCache.OnUpdateCorporateAction.Subscribe(this.UpdateByPosition, this)
    DataCache.OnRemoveCorporateAction.Subscribe(this.UpdateByPosition, this)

    DataCache.OnRecalculatedBalances.Subscribe(this.RecalculatedBalances, this)
}

PositionsBalancePanel.prototype.DCEventsUnSubscribe = function ()
{
    DataCache.OnAddPosition.UnSubscribe(this.UpdateByPosition, this)
    DataCache.OnUpdatePosition.UnSubscribe(this.UpdateByPosition, this)
    DataCache.OnRemovePosition.UnSubscribe(this.UpdateByPosition, this)

    //CorporateAction
    DataCache.OnAddCorporateAction.UnSubscribe(this.UpdateByPosition, this)
    DataCache.OnUpdateCorporateAction.UnSubscribe(this.UpdateByPosition, this)
    DataCache.OnRemoveCorporateAction.UnSubscribe(this.UpdateByPosition, this)

    DataCache.OnRecalculatedBalances.UnSubscribe(this.RecalculatedBalances, this)
}

PositionsBalancePanel.prototype.jbInit = function ()
{
    if (DataCache.AccountsCount === 1)
        this.topPanelHeight = 0;
    ApplicationPanelNew.prototype.jbInit.apply(this);

    this.createAccountLookup()

    this.quickTableRactive.quickTable.OnSelectionChanged.Subscribe(this.selectionChange, this);
};

PositionsBalancePanel.prototype.dispose = function ()
{
    this.quickTableRactive.quickTable.OnSelectionChanged.UnSubscribe(this.selectionChange, this);

    this.DCEventsUnSubscribe()

    ApplicationPanelNew.prototype.dispose.apply(this);
};

PositionsBalancePanel.prototype.SetColumnsColouringMode = function (table)
{
    this.ProcessSetColumnsColouringMode(table, [3], ColouringModes.Previous);
    this.ProcessSetColumnsColouringMode(table, [4], ColouringModes.Signed);
    this.ProcessSetColumnsColouringMode(table, [10], ColouringModes.Signed);
}

PositionsBalancePanel.prototype.createAccountLookup = function ()
{
    var accountsLookup = new TerceraAccountLookup();

    this.addControl(accountsLookup);
    accountsLookup.setBounds(5, 5, 200, 20);
    accountsLookup.onChangeValue.Subscribe(this.AddAllRows, this);
    accountsLookup.IsMultiSelect = true;
    accountsLookup.isMultiSelectMode = true;
    this.accLookup = accountsLookup;

    if (this.delayAccounts)
    {
        let accs = DataCache.Accounts,
            accIDArr = this.delayAccounts.split(','),
            accArr = [];

        for (let i = 0; i < accIDArr.length; i++)
            if (accs[accIDArr[i]] || accIDArr[i] === 'All')
                accArr.push(accs[accIDArr[i]]);

        accountsLookup.dropDownFormCallBack(accArr);
        this.delayAccounts = null
    }
    else
        accountsLookup.dropDownFormCallBack(new _AccountItemAll());

    this.ShowToolbarStateUpdate(this.ToolbarVisible)
}

PositionsBalancePanel.prototype.selectionChange = function ()
{
    let qt = this.quickTableRactive.quickTable
    let selectedRowsId = qt.selectedRowIds
    if (!selectedRowsId) return

    let insId = ''
    if (selectedRowsId.length > 0)
    {
        let row = qt.rows[selectedRowsId[0]]
        let item = row ? row.item : null
        if (item)
        {
            let ins = item.GetCurrentInstrument()
            if (ins) insId = ins.GetInteriorID()
        }
    }

    this.symbolLink_Out(false, insId)
};

PositionsBalancePanel.prototype.accountLink_In = function (accId)
{
    let acc = DataCache.Accounts[accId]
    if (acc && this.accLookup)
        this.accLookup.set('selectedItem', acc)
    this.AddAllRows();
};

PositionsBalancePanel.prototype.symbolLink_Out = function (newSubscriber, instrument)
{
    if (!instrument)
        return;

    var color = this.get('symbolLinkValue');
    if (color !== TerceraLinkControlConstants.STATE_NONE)
        LinkedSystem.setSymbol(color, instrument, newSubscriber);
};

PositionsBalancePanel.prototype.localize = function ()
{
    ApplicationPanelNew.prototype.localize.apply(this);
    if (!this.quickTableRactive)
        return;
    var contextItems = this.quickTableRactive.quickTable.tableContextMenuItems;

    if (!contextItems || contextItems.length === 0)
        return;

    for (let i = 0; i < contextItems.length; i++)
    {
        let item = contextItems[i]
        if (item)
        {
            if (item.locKey)
                item.text = Resources.getResource(item.locKey)

            if (item.subitems)
                for (let j = 0; j < item.subitems.length; j++)
                {
                    let subitem = item.subitems[j]
                    if (subitem.locKey)
                        subitem.text = Resources.getResource(subitem.locKey)
                }
        }
    }
}

PositionsBalancePanel.prototype.preparePopup = function ()
{
    ApplicationPanelNew.prototype.preparePopup.apply(this);
};

PositionsBalancePanel.prototype.populateTableContextMenu = function ()
{
    var items = [],
        moreThanOnAcc = DataCache.AccountsCount > 1;

    items.push(
        {
            locKey: 'panel.positions.menu.View',
            tag: 'View',
            enabled: true,
            subitems: [
                {
                    locKey: 'panel.positions.menu.ShowTotals',
                    tag: "ShowTotals",
                    checked: this.ShowTotals,
                    visible: !Resources.isHidden('contextMenu.Totals.visibility'),
                    enabled: true,
                    canCheck: true,
                    event: this.ShowTotalsStateChange.bind(this)
                },
                {
                    locKey: 'panel.positionsBalance.menu.ShowToolbar',
                    tag: "ShowToolbar",
                    checked: this.ToolbarVisible,
                    visible: !Resources.isHidden('panel.positionsBalance.menu.ShowToolbar') && moreThanOnAcc,
                    enabled: true,
                    canCheck: true,
                    event: this.ShowToolbarStateChange.bind(this)
                },
            ]
        }
    );

    this.AddSymbolInfoContextMenuItemIfNeed(items, false)

    this.menuTagDict = TerceraMenu.createTagDictionary(items)
    this.quickTableRactive.quickTable.setTableContextMenuItems(items);
};

PositionsBalancePanel.prototype.ShowToolbarStateChange = function (menuItem)
{
    this.ToolbarVisible = menuItem.checked

    this.ShowToolbarStateUpdate(this.ToolbarVisible);
}

PositionsBalancePanel.prototype.ShowToolbarStateUpdate = function (state)
{
    if (DataCache.AccountsCount === 1)
        state = false;
    this.accLookup.set("visible", state);
    this.topPanelHeight = state ? 27 : 0;
    this.layoutTable();
}

PositionsBalancePanel.prototype.disableAccountLinking = function ()     // overwrite base method   
{
    this.updateAccountLinkVisible(false)
};

PositionsBalancePanel.prototype.Properties = function ()
{
    var properties = ApplicationPanelNew.prototype.Properties.call(this);
    var acc = this.accLookup.get('selectedItem')

    if (!acc)
        return properties;
    let accNumber = ""
    if (acc._AccountItemAll || acc._AccountItemSelected)
        accNumber = acc.GetDataForSave()
    else
        accNumber = acc.AcctNumber

    properties.push(new DynProperty("account", accNumber, DynProperty.STRING, DynProperty.HIDDEN_GROUP));

    properties.push(new DynProperty("ShowTotals", this.quickTableRactive.quickTable.ShowTotals, DynProperty.BOOLEAN, DynProperty.HIDDEN_GROUP));
    properties.push(new DynProperty("ToolbarVisible", this.ToolbarVisible, DynProperty.BOOLEAN, DynProperty.HIDDEN_GROUP));

    return properties;
};

PositionsBalancePanel.prototype.callBack = function (newProperties)
{
    ApplicationPanelNew.prototype.callBack.call(this, newProperties);
    var dp = DynProperty.getPropertyByName(newProperties, 'account');
    if (dp && dp.value)
        this.delayAccounts = dp.value;

    this.ShowTotals = DynProperty.getPropValue(newProperties, "ShowTotals");
    this.ToolbarVisible = DynProperty.getPropValue(newProperties, "ToolbarVisible");
};


PositionsBalancePanel.prototype.RecalculatedBalances = function ()
{
    let qt = this.quickTableRactive.quickTable
    if (!qt) return
    let rows = qt.rows;
    for (let r in rows)
        rows[r].item.Recalc()
};